<template>
  <li
    class="border-line border-b last-of-type:border-none max-w-300 mx-auto group sm:max-w-1/2 lg:max-w-1/3"
  >
    <LangRouterLink
      :to="route"
      :class="[
        'grid gap-5 py-6 md:py-10 md:gap-15 transition-[padding]',
        { 'md:grid-cols-2': !forceVertical },
      ]"
      @mouseenter="prefetchResource"
    >
      <ResponsiveImage :media="item.image" :class="['rounded-lg w-full object-cover aspect-4/3']" />
      <div>
        <p
          v-if="date"
          class="mb-5 py-1.5 px-4 bg-(--accent-color) w-max max-w-full rounded-lg text-sm"
        >
          {{ date }}
        </p>
        <h2 class="text-xl font-semibold mb-4">{{ item.title }}</h2>
        <p :class="['text-grey-mid line-clamp-3', { 'md:line-clamp-none': !forceVertical }]">
          {{ item.description }}
        </p>
        <p class="text-(--main-color) flex items-center gap-1.5 mt-7">
          <span>{{ $t("read_more") }}</span>
          <IconSprite id="arrow" class="text-[10px] group-hover:ml-1 transition-[margin]" />
        </p>
      </div>
    </LangRouterLink>
  </li>
</template>

<script lang="ts">
import type { PropType } from "vue";
import { IconSprite, LangRouterLink, ResponsiveImage } from "@/components/helpers";
import i18n from "@/i18n";
import { useEventStore, useNewsStore } from "@/stores";

export default {
  name: "FeedCard",
  components: { IconSprite, LangRouterLink, ResponsiveImage },
  props: {
    item: {
      type: Object as PropType<Event | News>,
      required: true,
    },
    forceVertical: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    date(this: { item: Event | News; $t: (key: string) => string }) {
      if (!("start_date" in this.item)) return null;

      const start = new Date(this.item.start_date);
      const end = new Date(this.item.end_date);

      const sameDate = start.getDate() === end.getDate();
      const sameMonth = start.getMonth() === end.getMonth();
      const sameYear = start.getFullYear() === end.getFullYear();

      if (sameDate && sameMonth && sameYear) {
        return this.$t("date_intervals.same_day")
          .replace(
            ":date",
            start.toLocaleString(`${i18n.global.locale.value}-ca`, {
              day: "2-digit",
              month: "long",
            }),
          )
          .replace(
            ":start",
            start.toLocaleString(`${i18n.global.locale.value}-ca`, {
              hour: "numeric",
              minute: "numeric",
            }),
          )
          .replace(
            ":end",
            end.toLocaleString(`${i18n.global.locale.value}-ca`, {
              hour: "numeric",
              minute: "numeric",
            }),
          );
      }

      return this.$t("date_intervals.different_day")
        .replace(
          ":start",
          start.toLocaleString(`${i18n.global.locale.value}-ca`, {
            day: "numeric",
            month: sameMonth && sameYear ? undefined : "long",
            year: sameYear ? undefined : "numeric",
          }),
        )
        .replace(
          ":end",
          end.toLocaleString(`${i18n.global.locale.value}-ca`, {
            day: "numeric",
            month: "long",
            year: "numeric",
          }),
        );
    },
    route() {
      const currentLocale = i18n.global.locale.value as "en" | "fr";
      return "start_date" in this.item
        ? { name: this.$routes.EVENT.SHOW, params: { slug: this.item.slug[currentLocale] } }
        : { name: this.$routes.NEWS.SHOW, params: { slug: this.item.slug[currentLocale] } };
    },
  },
  methods: {
    prefetchResource() {
      const currentLocale = i18n.global.locale.value as "en" | "fr";
      if ("start_date" in this.item) {
        useEventStore().get(this.item.slug[currentLocale]);
      } else {
        useNewsStore().get(this.item.slug[currentLocale]);
      }
    },
  },
};
</script>
